"use client"

import { useState, useEffect } from "react"
import { Plus, Edit2, Trash2, Check, X, BookOpen, StarIcon, Sparkles } from "lucide-react"
import { createStar, getStar, updateStar, deleteStar as apiDeleteStar } from "../utils/api"

export default function Star() {
  const [stars, setStars] = useState([])
  const [newStar, setNewStar] = useState("")
  const [fetching, setFetching] = useState(true)
  const [adding, setAdding] = useState(false)
  const [error, setError] = useState(null)
  // Per-item action tracking
  const [editingId, setEditingId] = useState(null)
  const [editingName, setEditingName] = useState("")
  const [savingId, setSavingId] = useState(null)
  const [deletingId, setDeletingId] = useState(null)

  // Fetch stars on mount
  useEffect(() => {
    const fetchStars = async () => {
      try {
        setFetching(true)
        const res = await getStar()
        const formatted = res.data.map((s) => ({ ...s, id: s._id }))
        setStars(formatted)
      } catch (err) {
        console.error(err)
        setError("Failed to fetch stars")
      } finally {
        setFetching(false)
      }
    }
    fetchStars()
  }, [])

  const addStar = async () => {
    if (!newStar.trim()) return
    try {
      setAdding(true)
      const res = await createStar({ name: newStar.trim() })
      setStars((prev) => [...prev, { ...res.data, id: res.data._id }])
      setNewStar("")
    } catch (err) {
      console.error(err)
      setError("Failed to add star")
    } finally {
      setAdding(false)
    }
  }

  const startEditing = (star) => {
    setEditingId(star.id)
    setEditingName(star.name)
  }

  const saveEdit = async () => {
    if (!editingName.trim()) return
    try {
      setSavingId(editingId)
      await updateStar(editingId, { name: editingName.trim() })
      setStars((prev) => prev.map((s) => (s.id === editingId ? { ...s, name: editingName.trim() } : s)))
      cancelEdit()
    } catch (err) {
      console.error(err)
      setError("Failed to update star")
    } finally {
      setSavingId(null)
    }
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditingName("")
  }

  const deleteStar = async (id) => {
    try {
      setDeletingId(id)
      await apiDeleteStar(id)
      setStars((prev) => prev.filter((s) => s.id !== id))
    } catch (err) {
      console.error(err)
      setError("Failed to delete star")
    } finally {
      setDeletingId(null)
    }
  }

  const handleKeyPress = (e, action) => e.key === "Enter" && action()

  if (fetching) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4"></div>
          <p className="text-lg font-semibold text-gray-700">Loading stars...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md mx-4">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <X className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Stars</h3>
            <p className="text-red-600">{error}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-pink-50 py-8 px-4">
      <div className="w-full mx-auto space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="p-3 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl shadow-lg">
              <StarIcon className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Stars Management
            </h1>
          </div>
          <p className="text-gray-600 text-lg">Manage zodiac stars and celestial bodies</p>
        </div>

        {/* Add Star Section */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">
            <div className="flex items-center gap-2">
              <Plus className="h-6 w-6" />
              <h2 className="text-xl font-semibold">Add New Star</h2>
            </div>
          </div>
          <div className="p-6">
            <div className="flex gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Enter star name..."
                  value={newStar}
                  onChange={(e) => setNewStar(e.target.value)}
                  onKeyPress={(e) => handleKeyPress(e, addStar)}
                  className="w-full h-12 px-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                  disabled={adding}
                />
              </div>
              <button
                onClick={addStar}
                disabled={!newStar.trim() || adding}
                className="h-12 px-8 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl font-semibold disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg hover:shadow-xl transition-all duration-200"
              >
                {adding ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="w-5 h-5" />
                    Add Star
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Stars List */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-6 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="h-6 w-6" />
                <h2 className="text-xl font-semibold">Stars List</h2>
              </div>
              <div className="bg-white/20 px-4 py-2 rounded-full">
                <span className="font-semibold">
                  {stars.length} {stars.length === 1 ? "star" : "stars"}
                </span>
              </div>
            </div>
          </div>

          <div className="p-6">
            {stars.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-24 h-24 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <BookOpen className="w-12 h-12 text-purple-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Stars Added Yet</h3>
                <p className="text-gray-600">Start by adding your first star above</p>
              </div>
            ) : (
              <div className="space-y-4">
                {stars.map((star, idx) => (
                  <div
                    key={star.id}
                    className="group bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-xl p-4 transition-all duration-200 hover:shadow-md"
                  >
                    {editingId === star.id ? (
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-10 h-10 bg-purple-100 rounded-lg">
                          <span className="text-purple-600 font-bold text-sm">{String(idx + 1).padStart(2, "0")}</span>
                        </div>
                        <input
                          type="text"
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          onKeyPress={(e) => handleKeyPress(e, saveEdit)}
                          className="flex-1 h-10 px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          autoFocus
                          disabled={savingId === star.id}
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={saveEdit}
                            disabled={!editingName.trim() || savingId === star.id}
                            className="h-10 px-4 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-lg font-medium disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed flex items-center gap-2 shadow-md hover:shadow-lg transition-all duration-200"
                          >
                            {savingId === star.id ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                Saving...
                              </>
                            ) : (
                              <>
                                <Check className="w-4 h-4" />
                                Save
                              </>
                            )}
                          </button>
                          <button
                            onClick={cancelEdit}
                            disabled={savingId === star.id}
                            className="h-10 px-4 bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white rounded-lg font-medium flex items-center gap-2 shadow-md hover:shadow-lg transition-all duration-200"
                          >
                            <X className="w-4 h-4" />
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="flex items-center justify-center w-10 h-10 bg-purple-100 rounded-lg">
                            <span className="text-purple-600 font-bold text-sm">
                              {String(idx + 1).padStart(2, "0")}
                            </span>
                          </div>
                          <div className="flex items-center gap-3">
                            <StarIcon className="w-5 h-5 text-yellow-500" />
                            <span className="font-semibold text-gray-900 text-lg">{star.name}</span>
                          </div>
                        </div>
                        <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          <button
                            onClick={() => startEditing(star)}
                            disabled={deletingId === star.id}
                            className="h-10 px-4 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white rounded-lg font-medium flex items-center gap-2 shadow-md hover:shadow-lg transition-all duration-200"
                          >
                            <Edit2 className="w-4 h-4" />
                            Edit
                          </button>
                          <button
                            onClick={() => deleteStar(star.id)}
                            disabled={deletingId === star.id}
                            className="h-10 px-4 bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white rounded-lg font-medium flex items-center gap-2 shadow-md hover:shadow-lg transition-all duration-200"
                          >
                            {deletingId === star.id ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                Deleting...
                              </>
                            ) : (
                              <>
                                <Trash2 className="w-4 h-4" />
                                Delete
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}